/**
 * Procedural-fallback colour palette for Phase 1 rendering.
 *
 * Per Q-C8 resolution, the `PackResolver` is internal to P1.7: pack sprite
 * → procedural + theme → procedural + defaults. Since no sprite atlas ships
 * with Phase 1 yet, every surface renders from the procedural layer here.
 * Real art lands in Phase 4 polish.
 *
 * MaterialId / BiomeId values are `u32` newtypes — in practice Phase 1
 * populates them from `data/materials.toml` and the biome classifier
 * respectively, using small integer indices into the catalogue below.
 */

/** Fallback colour used when a MaterialId / BiomeId / PropKind falls off
 *  the end of its palette. Vivid magenta so it's obvious during dev. */
export const PALETTE_FALLBACK_COLOR = 0xff00ff;

/**
 * Material palette (14 slots per WORLD_MODEL §3 / CLIENT_DESIGN §4.2).
 * Index 0 is Grass — the worldgen default — so new empty worlds paint
 * green rather than the fallback magenta.
 */
export const MATERIAL_COLORS: readonly number[] = [
  0x5b8c52, // 0  Grass
  0x7d7a77, // 1  Stone
  0x8b5a2b, // 2  Wood
  0xd1c8b5, // 3  Tile
  0x8b6f47, // 4  Dirt
  0xe6d49a, // 5  Sand
  0x336ea8, // 6  Water
  0x6b5028, // 7  Tilled Soil
  0x707070, // 8  Cobblestone
  0xf0f5fa, // 9  Snow
  0x5d4a33, // 10 Mud
  0xa5836b, // 11 Clay
  0x9d6a5e, // 12 Carpet
  0xe8e5dc, // 13 Marble
  0xb9a182, // 14 Path
];

export function materialColor(id: number): number {
  return MATERIAL_COLORS[id] ?? PALETTE_FALLBACK_COLOR;
}

/**
 * Biome palette — subtle ambient tints applied over the material base.
 * Slight multipliers, not replacements; used for faint desaturation /
 * hue shift (Phase 1.5 autotile blending replaces this with edge overlays).
 */
export const BIOME_TINTS: readonly number[] = [
  0xffffff, // 0  Grassland — no-op tint
  0xd5dbc4, // 1  Forest — greener
  0xbfd6e0, // 2  Coast — bluer
  0xefe1b4, // 3  Desert — yellower
  0xdce7ea, // 4  Tundra — whiter
  0x90a090, // 5  Swamp — muted green
  0xc4bfb5, // 6  Mountain — grey
  0x88adc8, // 7  Ocean — strong blue
];

export function biomeTint(id: number): number {
  return BIOME_TINTS[id] ?? 0xffffff;
}

/** Cave tile palette keyed by RockKind string. */
export const ROCK_KIND_COLORS: Record<string, number> = {
  Granite:   0x6e6e6e,
  Basalt:    0x3a3a3a,
  Limestone: 0x9e9682,
  Sandstone: 0x8b7355,
  Slate:     0x505a60,
  Marble:    0xa8a8b0,
  Schist:    0x5a6a5a,
  Quartzite: 0x8a8a7a,
};

export function rockKindColor(kind: string): number {
  return ROCK_KIND_COLORS[kind] ?? PALETTE_FALLBACK_COLOR;
}

/** Wall-material palette (Phase 1 ships Wood + Stone — WORLD_MODEL §4.2). */
export const WALL_MATERIAL_COLORS: Record<string, number> = {
  Wood: 0x8b5a2b,
  Stone: 0x7d7a77,
  Plaster: 0xd1c8b5,
  Brick: 0x9c5a48,
  Thatch: 0xc8a655,
  Log: 0x6b4a2b,
};

export function wallMaterialColor(material: string): number {
  return WALL_MATERIAL_COLORS[material] ?? PALETTE_FALLBACK_COLOR;
}

/**
 * Prop kind → colour rectangle. PropKind is a `u32` on the wire (indices
 * into `data/props.toml`); until the pack atlas lands, we render each prop
 * as a small coloured rectangle with the kind printed on it. The mapping
 * below is intentionally coarse — everything collapses to half-a-dozen
 * bucket colours and the label carries the real information.
 */
export function propKindColor(kind: number): number {
  // Stable hash-to-colour so the same PropKind always gets the same swatch.
  // Kept in the "warm-cozy" palette family (CLIENT_DESIGN §1).
  const bucket = kind % 8;
  switch (bucket) {
    case 0:
      return 0x8b5a2b; // brown
    case 1:
      return 0x5b8c52; // green
    case 2:
      return 0xc6a664; // warm yellow
    case 3:
      return 0x8b3a3a; // rust
    case 4:
      return 0x4a5d7a; // muted blue
    case 5:
      return 0x6b5e88; // muted purple
    case 6:
      return 0x9e7b52; // tan
    default:
      return 0x707070; // grey
  }
}

export type Season = 'Spring' | 'Summer' | 'Autumn' | 'Winter';

const SEASONAL_MODULATION: Record<Season, { r: number; g: number; b: number }> = {
  Spring: { r: 0.95, g: 1.05, b: 0.95 },
  Summer: { r: 1.05, g: 1.0, b: 0.9 },
  Autumn: { r: 1.1, g: 0.95, b: 0.85 },
  Winter: { r: 0.9, g: 0.95, b: 1.05 },
};

export interface MoistureVisual {
  darken: number;
  puddle: boolean;
}

export function moistureDarken(moisture: number): MoistureVisual {
  const darken = Math.min(0.35, moisture / 255 * 0.4);
  const puddle = moisture >= 200;
  return { darken, puddle };
}

export function blightDesaturate(color: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const grey = Math.round(r * 0.3 + g * 0.59 + b * 0.11);
  const br = Math.min(255, Math.round(grey * 0.6 + 0.4 * 0x6b));
  const bg = Math.min(255, Math.round(grey * 0.6 + 0.4 * 0x50));
  const bb = Math.min(255, Math.round(grey * 0.6 + 0.4 * 0x28));
  return (br << 16) | (bg << 8) | bb;
}

export const SPOILAGE_STAGES = new Set([
  'fresh', 'stale', 'spoiled', 'rotten',
]);

interface SpoilageParams {
  desaturation: number;
  tintColor: number;
  tintStrength: number;
}

const SPOILAGE_VISUAL: Record<string, SpoilageParams> = {
  stale:   { desaturation: 0.20, tintColor: 0xb89830, tintStrength: 0.15 },
  spoiled: { desaturation: 0.40, tintColor: 0x708038, tintStrength: 0.30 },
  rotten:  { desaturation: 0.60, tintColor: 0x4a3a2a, tintStrength: 0.45 },
};

export function spoilageTint(color: number, stage: string | null): number {
  if (stage === null) return color;
  const params = SPOILAGE_VISUAL[stage];
  if (!params) return color;

  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;

  const grey = Math.round(r * 0.3 + g * 0.59 + b * 0.11);
  const dr = Math.round(r * (1 - params.desaturation) + grey * params.desaturation);
  const dg = Math.round(g * (1 - params.desaturation) + grey * params.desaturation);
  const db = Math.round(b * (1 - params.desaturation) + grey * params.desaturation);

  const tr = (params.tintColor >> 16) & 0xff;
  const tg = (params.tintColor >> 8) & 0xff;
  const tb = params.tintColor & 0xff;

  const fr = Math.min(255, Math.round(dr * (1 - params.tintStrength) + tr * params.tintStrength));
  const fg = Math.min(255, Math.round(dg * (1 - params.tintStrength) + tg * params.tintStrength));
  const fb = Math.min(255, Math.round(db * (1 - params.tintStrength) + tb * params.tintStrength));

  return (fr << 16) | (fg << 8) | fb;
}

export function seasonalTint(color: number, season: Season): number {
  const mod = SEASONAL_MODULATION[season];
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * mod.r));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * mod.g));
  const b = Math.min(255, Math.round((color & 0xff) * mod.b));
  return (r << 16) | (g << 8) | b;
}
